import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, HeartPulse, Store, ClipboardCheck, ShieldCheck, AlertTriangle } from "lucide-react";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-md px-5 py-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-xl bg-primary grid place-items-center text-primary-foreground"><HeartPulse className="h-5 w-5" /></div>
          <div className="font-heading font-bold leading-tight">Mon bilan prévention<div className="text-[11px] font-normal text-muted-foreground">Pharmacie des Écoles</div></div>
        </div>
        {children}
      </div>
      <footer className="border-t bg-muted/30 text-[11px] text-muted-foreground px-4 py-4 text-center space-y-1">
        <p className="font-semibold text-destructive">En cas d'urgence, contactez le 15 ou le 112.</p>
        <p>Ce service ne recueille aucune donnée de santé en ligne. Votre bilan est réalisé au comptoir par un pharmacien. Ce parcours ne constitue pas un diagnostic.</p>
      </footer>
    </div>
  );
}

export default function BilanPrevention() {
  const navigate = useNavigate();
  const [step, setStep] = useState("intro");
  const [ageBand, setAgeBand] = useState(null);

  const BANDS = [["18_25", "18–25 ans"], ["45_50", "45–50 ans"], ["60_65", "60–65 ans"], ["70_75", "70–75 ans"]];
  const eligible = ageBand && ageBand !== "autre";

  // Suivi anonyme : enregistre « bilan commencé » (aucune donnée de santé ni identité).
  const startNow = async () => {
    if (!eligible) return;
    api.post("/public/bilan-sessions").catch(() => {});
    setStep("counter");
  };

  // Écran 1 — invitation à réaliser le bilan tout de suite
  if (step === "intro") {
    return (
      <Shell>
        <button onClick={() => navigate("/")} data-testid="bp-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4" /> Retour</button>
        <div className="h-14 w-14 rounded-2xl bg-primary/10 grid place-items-center text-primary mb-4"><HeartPulse className="h-7 w-7" /></div>
        <h1 className="font-heading text-3xl font-bold mb-2" data-testid="bp-intro-title">Réaliser mon bilan de prévention</h1>
        <p className="text-sm text-muted-foreground mb-6">Le bilan de prévention est un moment d'échange avec votre pharmacien pour faire le point sur votre santé et recevoir des conseils personnalisés. Il est réalisé directement à la pharmacie.</p>
        <Card className="rounded-2xl p-4 mb-6 space-y-3">
          <div className="flex items-start gap-3 text-sm"><ClipboardCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" /><span>Un questionnaire papier vous est remis au comptoir par le pharmacien.</span></div>
          <div className="flex items-start gap-3 text-sm"><ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" /><span>Aucune donnée de santé n'est saisie ni conservée sur ce site : tout se fait avec le pharmacien.</span></div>
        </Card>
        <div className="mt-2 mb-2">
          <p className="text-sm font-semibold text-foreground">Votre tranche d'âge</p>
          <p className="text-xs text-muted-foreground mb-3">Pour vérifier votre éligibilité au bilan de prévention pris en charge par l'Assurance Maladie.</p>
          <div className="grid grid-cols-2 gap-2" data-testid="bp-age-options">
            {BANDS.map(([v, l]) => (
              <button key={v} data-testid={`bp-age-${v}`} onClick={() => setAgeBand(v)}
                className={`rounded-xl border-2 p-3 text-sm font-medium transition-colors ${ageBand === v ? "border-primary bg-primary/5 text-primary" : "border-input hover:border-primary/50"}`}>{l}</button>
            ))}
            <button data-testid="bp-age-autre" onClick={() => setAgeBand("autre")}
              className={`col-span-2 rounded-xl border-2 p-3 text-sm font-medium transition-colors ${ageBand === "autre" ? "border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-950/30" : "border-input hover:border-primary/50"}`}>Autre âge</button>
          </div>
        </div>

        {ageBand === "autre" ? (
          <div className="mt-5" data-testid="bp-ineligible">
            <Card className="rounded-2xl p-4 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <span className="text-sm text-amber-800 dark:text-amber-200">Le bilan de prévention pris en charge par l'Assurance Maladie est réservé à ces tranches d'âge. Contactez la pharmacie pour d'autres services.</span>
            </Card>
            <Button data-testid="bp-contact" variant="outline" onClick={() => navigate("/contact")} className="w-full h-12 rounded-2xl mt-3">Contacter la pharmacie</Button>
          </div>
        ) : (
          <>
            <Button data-testid="bp-start-now" onClick={startNow} disabled={!eligible} className="w-full h-14 rounded-2xl gap-2 mt-5">Réaliser mon bilan tout de suite <ArrowRight className="h-5 w-5" /></Button>
            <p className="text-xs text-muted-foreground text-center mt-3" data-testid="bp-start-note">En appuyant sur « Réaliser mon bilan tout de suite », vous commencez administrativement votre démarche de bilan. Il vous suffira ensuite de vous présenter au comptoir.</p>
          </>
        )}
      </Shell>
    );
  }

  // Écran 2 — rendez-vous au comptoir
  return (
    <Shell>
      <button onClick={() => setStep("intro")} data-testid="bp-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4" /> Retour</button>
      <div className="text-center" data-testid="bp-counter">
        <div className="h-16 w-16 rounded-full bg-primary/10 grid place-items-center mx-auto mb-4 text-primary"><Store className="h-8 w-8" /></div>
        <span className="inline-block text-xs font-bold uppercase tracking-[0.18em] bg-primary text-primary-foreground rounded-full px-4 py-1.5 mb-4" data-testid="bp-bilan-sante-badge">Bilan santé</span>
        <h1 className="font-heading text-2xl font-bold mb-3" data-testid="bp-counter-title">Votre bilan est administrativement commencé</h1>
        <p className="text-sm text-muted-foreground mb-6">Veuillez vous rendre au comptoir avec votre <b className="text-foreground">carte Vitale</b> pour effectuer votre <b className="text-foreground">bilan santé</b>.</p>
      </div>
      <Card className="rounded-2xl p-5 mb-6 bg-primary/5 border-primary/30 text-sm text-center" data-testid="bp-counter-instruction">
        Dites simplement au pharmacien : <b>« J'ai commencé une démarche de bilan. »</b><br />Le pharmacien se chargera du reste.
      </Card>
      <Button data-testid="bp-home" variant="outline" onClick={() => navigate("/")} className="w-full h-12 rounded-2xl">Retour à l'accueil</Button>
    </Shell>
  );
}
